import React from 'react';
import {Trans, useTranslation} from 'next-i18next';
import {Button, ListItem, Text, UnorderedList, VStack} from '@tempo/core';

import type {FormFieldChange, LabelFormData, SubLabel} from '../../types';
import {useClaims} from '@deezer/next/contexts/main/hooks';
import {ArrowRightIcon, PlusIcon} from '@tempo/icons';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import FooterRow from '@components/PageElements/FooterRow';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import FormSectionTitle from '@components/PageElements/FormSectionTitle';
import FormContainer from '@components/PageElements/FormContainer';
import FormSection from '@components/PageElements/FormSection';
import FormSectionContainer from '@components/PageElements/FormSectionContainer';
import AutoIconButton from '@components/AutoIconButton';
import LabelTable from '@deezer/next/features/ClaimAccess/components/LabelTable';

type LabelListFormCardProps = {
	formData: LabelFormData;
	onFormFieldChange: FormFieldChange<LabelFormData>;
	onAddLabel: () => void;
	onEditLabel: (label: SubLabel) => void;
	onBack: () => void;
	onSubmit: () => void;
	isSubmitting: boolean;
};

const LabelListFormCard = ({
	formData,
	onFormFieldChange,
	onAddLabel,
	onEditLabel,
	onBack,
	onSubmit,
	isSubmitting,
}: LabelListFormCardProps) => {
	const {t} = useTranslation('d4c');
	const claims = useClaims();
	const handleLabelEdit = (label: SubLabel) => {
		onEditLabel(label);
	};

	const handleLabelRemove = (label: SubLabel) => {
		onFormFieldChange(
			'labelSubLabels',
			formData.labelSubLabels.filter(({id}) => id !== label.id),
		);
	};

	const handleSubmit = () => {
		if (isSubmitting) return;
		onSubmit();
	};

	return (
		<PageLayout
			data-testid="label-list-card"
			header={<TitleRow title={t('requestaccess_title_newrequest_d4c-web')} />}
			footer={
				<FooterRow
					rightElements={
						<>
							{claims.length > 0 && (
								<Button
									data-testid="back-button"
									variant="outline"
									onClick={onBack}
									leftIcon={<ArrowRightIcon transform="scaleX(-1)" />} // todo fix that
									size="small"
								>
									{t('generic_action_back_d4c-web')}
								</Button>
							)}
							<Button
								data-testid="send-request-button"
								onClick={handleSubmit}
								isDisabled={
									!formData.labelSubLabels ||
									formData.labelSubLabels.length === 0
								}
								isLoading={isSubmitting}
								size="small"
							>
								{t('generic_action_sendrequest_d4c-web')}
							</Button>
						</>
					}
				/>
			}
		>
			<FormContainer>
				<FormSection>
					<FormSectionTitle
						title={t('requestaccesslabel_title_manageyourlabels_d4c-web')}
						rightElements={
							<AutoIconButton
								data-testid="add-button"
								aria-label="add new label"
								onClick={onAddLabel}
								size="small"
								leftIcon={<PlusIcon />}
							>
								{t('requestaccesslabel_title_addnewlabel_d4c-web')}
							</AutoIconButton>
						}
					/>
					<VStack align="left" spacing="spacing.s">
						<Text>
							{t('requestaccesslabel_text_toclaimartistaslabelprovide_d4c-web')}
						</Text>
						<UnorderedList>
							<ListItem>
								<Trans
									components={{
										strong: <strong />,
									}}
								>
									{t('requestaccesslabel_text_livereleaseupc_d4c-web')}
								</Trans>
							</ListItem>
							<ListItem>
								{t('requestaccesslabel_text_releasedistributor_d4c-web')}
							</ListItem>
							<ListItem>
								{t('requestaccesslabel_text_releaselabelexactname_d4c-web')}
							</ListItem>
						</UnorderedList>
						<Text>
							{t(
								'requestaccesslabel_text_accessonlytolabeldistributorpairprovided_d4c-web',
							)}
						</Text>
						<Text>
							{t('requestaccesslabel_text_labelnamemustmatchexactly_d4c-web')}
						</Text>
						<Text>
							{t(
								'requestaccesslabel_text_contactinternadmintorequestaccess_d4c-web',
							)}
						</Text>
					</VStack>
					<FormSectionContainer>
						<LabelTable
							labels={formData.labelSubLabels}
							onLabelEdit={handleLabelEdit}
							onLabelRemove={handleLabelRemove}
						/>
					</FormSectionContainer>
				</FormSection>
			</FormContainer>
		</PageLayout>
	);
};

export default LabelListFormCard;
